/**
 * HousingStaffManager - Manage housing/accommodation staff
 * Includes role-based permissions for housing operations
 * Supports permanent staff and event-specific temporary staff
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Users, Plus, Edit2, Trash2, Building2, Search,
  Key, Shield, UserCheck, Save, Eye, EyeOff, Calendar, Clock, Upload
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import QuickStaffOnboarding from '../QuickStaffOnboarding';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HousingStaffManager = () => {
  const queryClient = useQueryClient();
  const [showStaffDialog, setShowStaffDialog] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [activeTab, setActiveTab] = useState('permanent');
  const [searchTerm, setSearchTerm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // Staff form state
  const [staffForm, setStaffForm] = useState({
    email: '',
    password: '',
    name: '',
    phone: '',
    role: 'front_desk',
    is_active: true,
    is_temporary: false,
    event_id: ''
  });

  const token = getToken();
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Fetch staff
  const { data: staff = [], isLoading } = useQuery({
    queryKey: ['housing-staff'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/accommodation/staff`, { headers });
      if (!res.ok) return [];
      return res.json();
    }
  });

  // Fetch roles
  const { data: rolesData } = useQuery({
    queryKey: ['housing-roles'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/accommodation/roles`, { headers });
      if (!res.ok) return { housing_roles: [] };
      return res.json();
    }
  });

  // Fetch events for temporary staff assignment
  const { data: events = [] } = useQuery({
    queryKey: ['events-list'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/events`, { headers });
      if (!res.ok) return [];
      return res.json();
    }
  });

  const housingRoles = rolesData?.housing_roles || [
    { id: 'housing_manager', name: 'Housing Manager', permissions: ['housing.view_rooms', 'housing.manage_rooms', 'housing.view_bookings', 'housing.manage_bookings', 'housing.check_in', 'housing.check_out', 'housing.manage_guests', 'housing.view_reports', 'housing.manage_staff'] },
    { id: 'front_desk', name: 'Front Desk', permissions: ['housing.view_rooms', 'housing.view_bookings', 'housing.check_in', 'housing.check_out', 'housing.manage_guests'] },
    { id: 'housekeeping', name: 'Housekeeping', permissions: ['housing.view_rooms', 'housing.update_room_status'] },
    { id: 'viewer', name: 'Viewer', permissions: ['housing.view_rooms', 'housing.view_bookings'] }
  ];

  // Create staff
  const createStaffMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/api/admin/accommodation/staff`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to create staff');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['housing-staff']);
      setShowStaffDialog(false);
      resetForm();
      toast.success('Staff member created!');
    },
    onError: (error) => toast.error(error.message)
  });

  // Update staff
  const updateStaffMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const res = await fetch(`${API_URL}/api/admin/accommodation/staff/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to update');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['housing-staff']);
      setShowStaffDialog(false);
      resetForm();
      toast.success('Staff updated!');
    },
    onError: (error) => toast.error(error.message)
  });

  // Delete staff
  const deleteStaffMutation = useMutation({
    mutationFn: async (id) => {
      const res = await fetch(`${API_URL}/api/admin/accommodation/staff/${id}`, {
        method: 'DELETE',
        headers
      });
      if (!res.ok) throw new Error('Failed to delete');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['housing-staff']);
      toast.success('Staff deleted!');
    }
  });

  // Reset password
  const resetPasswordMutation = useMutation({
    mutationFn: async (id) => {
      const res = await fetch(`${API_URL}/api/admin/accommodation/staff/${id}/reset-password`, {
        method: 'POST',
        headers
      });
      if (!res.ok) throw new Error('Failed to reset password');
      return res.json();
    },
    onSuccess: (data) => {
      toast.success(data.message);
    }
  });

  const resetForm = () => {
    setStaffForm({
      email: '',
      password: '',
      name: '',
      phone: '',
      role: 'front_desk',
      is_active: true,
      is_temporary: activeTab === 'temporary',
      event_id: ''
    });
    setSelectedStaff(null);
    setShowPassword(false);
  };

  const openEditDialog = (staffMember) => {
    setSelectedStaff(staffMember);
    setStaffForm({
      email: staffMember.email,
      password: '',
      name: staffMember.name,
      phone: staffMember.phone || '',
      role: staffMember.role,
      is_active: staffMember.is_active !== false,
      is_temporary: staffMember.is_temporary || false,
      event_id: staffMember.event_id || ''
    });
    setShowStaffDialog(true);
  };

  const openCreateDialog = (isTemporary = false) => {
    resetForm();
    setStaffForm(prev => ({
      ...prev,
      is_temporary: isTemporary
    }));
    setShowStaffDialog(true);
  };

  const handleSave = () => {
    if (!staffForm.name || !staffForm.email) {
      toast.error('Name and email are required');
      return;
    }
    if (!selectedStaff && !staffForm.password) {
      toast.error('Password is required for new staff');
      return;
    }

    if (selectedStaff) {
      const updateData = {
        name: staffForm.name,
        phone: staffForm.phone,
        role: staffForm.role,
        is_active: staffForm.is_active
      };
      updateStaffMutation.mutate({ id: selectedStaff.id, data: updateData });
    } else {
      createStaffMutation.mutate(staffForm);
    }
  };

  const permanentStaff = staff.filter(s => !s.is_temporary);
  const temporaryStaff = staff.filter(s => s.is_temporary);

  const filteredPermanentStaff = permanentStaff.filter(s =>
    s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredTemporaryStaff = temporaryStaff.filter(s =>
    s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const roleColors = {
    housing_manager: 'bg-purple-100 text-purple-800',
    front_desk: 'bg-blue-100 text-blue-800',
    housekeeping: 'bg-yellow-100 text-yellow-800',
    viewer: 'bg-gray-100 text-gray-800'
  };

  const StaffCard = ({ staffMember }) => (
    <Card className={`${!staffMember.is_active ? 'opacity-60' : ''}`} data-testid={`staff-card-${staffMember.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full flex items-center justify-center bg-indigo-100">
              <Building2 className="h-6 w-6 text-indigo-600" />
            </div>
            <div>
              <h3 className="font-semibold">{staffMember.name}</h3>
              <p className="text-sm text-gray-500">{staffMember.email}</p>
              <div className="flex items-center gap-2 mt-1">
                <Badge className={roleColors[staffMember.role] || 'bg-gray-100'}>
                  {staffMember.role_name || staffMember.role?.replace('_', ' ')}
                </Badge>
                {staffMember.is_temporary && (
                  <Badge variant="outline" className="text-xs border-orange-300 text-orange-600">
                    <Clock className="h-3 w-3 mr-1" />
                    Temp
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" onClick={() => openEditDialog(staffMember)}>
              <Edit2 className="h-4 w-4" />
            </Button>
            <Button 
              size="sm" 
              variant="ghost"
              onClick={() => resetPasswordMutation.mutate(staffMember.id)}
              title="Reset password"
            >
              <Key className="h-4 w-4" />
            </Button>
            <Button 
              size="sm" 
              variant="ghost"
              className="text-red-600"
              onClick={() => {
                if (window.confirm('Are you sure you want to delete this staff member?')) {
                  deleteStaffMutation.mutate(staffMember.id);
                }
              }}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        {/* Permissions */}
        <div className="mt-3 pt-3 border-t">
          <p className="text-xs font-medium text-gray-500 mb-1">Permissions:</p>
          <div className="flex flex-wrap gap-1">
            {staffMember.permissions?.slice(0, 4).map((perm, idx) => (
              <Badge key={idx} variant="outline" className="text-xs">
                {perm.split('.')[1]?.replace('_', ' ')}
              </Badge>
            ))}
            {staffMember.permissions?.length > 4 && (
              <Badge variant="outline" className="text-xs">
                +{staffMember.permissions.length - 4}
              </Badge>
            )}
          </div>
        </div>

        {staffMember.last_login && (
          <p className="text-xs text-gray-400 mt-2">
            Last login: {new Date(staffMember.last_login).toLocaleDateString()}
          </p>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6" data-testid="housing-staff-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Users className="h-6 w-6 text-indigo-600" />
            Housing Staff Management
          </h2>
          <p className="text-sm text-gray-500">Manage permanent and event-specific temporary staff</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Search staff..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="staff-search-input"
        />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="permanent" className="gap-2">
              <Building2 className="h-4 w-4" />
              Permanent Staff ({permanentStaff.length})
            </TabsTrigger>
            <TabsTrigger value="temporary" className="gap-2">
              <Calendar className="h-4 w-4" />
              Event Staff ({temporaryStaff.length})
            </TabsTrigger>
          </TabsList>
          <div className="flex gap-2">
            <QuickStaffOnboarding staffType="housing" />
            <Button onClick={() => openCreateDialog(activeTab === 'temporary')} data-testid="add-housing-staff-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add {activeTab === 'temporary' ? 'Event' : 'Permanent'} Staff
            </Button>
          </div>
        </div>

        {/* Permanent Staff Tab */}
        <TabsContent value="permanent" className="mt-4">
          {isLoading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : filteredPermanentStaff.length === 0 ? (
            <Card className="py-12">
              <CardContent className="text-center">
                <Building2 className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500">No permanent staff yet</p>
                <Button variant="link" onClick={() => openCreateDialog(false)}>
                  Add your first housing staff member
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredPermanentStaff.map((s) => (
                <StaffCard key={s.id} staffMember={s} />
              ))}
            </div>
          )}
          
          {/* Roles Reference */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <Shield className="h-4 w-4" />
                Housing Roles Reference
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {housingRoles.map((role) => (
                  <div key={role.id} className="p-3 bg-gray-50 rounded-lg">
                    <Badge className={roleColors[role.id]}>{role.name}</Badge>
                    <ul className="mt-2 text-xs text-gray-600 space-y-1">
                      {role.permissions?.slice(0, 4).map((p, i) => (
                        <li key={i} className="flex items-center gap-1">
                          <UserCheck className="h-3 w-3 text-green-500" />
                          {p.split('.')[1]?.replace('_', ' ')}
                        </li>
                      ))}
                      {role.permissions?.length > 4 && (
                        <li className="text-gray-400">+{role.permissions.length - 4} more</li>
                      )}
                    </ul>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Temporary/Event Staff Tab */}
        <TabsContent value="temporary" className="mt-4">
          {isLoading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : filteredTemporaryStaff.length === 0 ? (
            <Card className="py-12">
              <CardContent className="text-center">
                <Calendar className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500">No event-specific staff yet</p>
                <p className="text-sm text-gray-400 mt-1">Add temporary staff for specific events</p>
                <Button variant="link" onClick={() => openCreateDialog(true)}>
                  Add event staff member
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredTemporaryStaff.map((s) => (
                <StaffCard key={s.id} staffMember={s} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Staff Dialog */}
      <Dialog open={showStaffDialog} onOpenChange={setShowStaffDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedStaff ? 'Edit Staff' : `New ${staffForm.is_temporary ? 'Event' : 'Permanent'} Staff`}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={staffForm.name}
                onChange={(e) => setStaffForm({ ...staffForm, name: e.target.value })}
                placeholder="Full name"
                data-testid="input-staff-name"
              />
            </div>
            
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={staffForm.email}
                onChange={(e) => setStaffForm({ ...staffForm, email: e.target.value })}
                placeholder="email@example.com"
                disabled={!!selectedStaff}
                data-testid="input-staff-email"
              />
            </div>
            
            {!selectedStaff && (
              <div>
                <Label>Password *</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={staffForm.password}
                    onChange={(e) => setStaffForm({ ...staffForm, password: e.target.value })}
                    placeholder="Initial password"
                    data-testid="input-staff-password"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            )}

            <div>
              <Label>Phone</Label>
              <Input
                value={staffForm.phone}
                onChange={(e) => setStaffForm({ ...staffForm, phone: e.target.value })}
                placeholder="Phone number"
              />
            </div>

            <div>
              <Label>Role</Label>
              <Select
                value={staffForm.role}
                onValueChange={(v) => setStaffForm({ ...staffForm, role: v })}
              >
                <SelectTrigger data-testid="select-staff-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {housingRoles.map(r => (
                    <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {staffForm.is_temporary && events.length > 0 && (
              <div>
                <Label>Assigned Event (Optional)</Label>
                <Select
                  value={staffForm.event_id}
                  onValueChange={(v) => setStaffForm({ ...staffForm, event_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select an event" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">No specific event</SelectItem>
                    {events.map(e => (
                      <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <Switch
                checked={staffForm.is_active}
                onCheckedChange={(checked) => setStaffForm({ ...staffForm, is_active: checked })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStaffDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleSave} 
              disabled={createStaffMutation.isPending || updateStaffMutation.isPending}
              data-testid="save-staff-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              {selectedStaff ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HousingStaffManager;
